// components/forms/UpdateProfileFormSection.tsx
import { useForm, Controller, type SubmitHandler } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Typography, Button, FloatingLabelInput } from "@/design-system/Atoms";
import {
  updateProfileSchema,
  type UpdateProfileSchema,
} from "@/validation-schemas/update-profile-schemas";
import { usePatientProfileFormSubmission } from "@/rtkQuery/features/hooks/usePatientProfileFormSubmission";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@/routes/routes.config";
import { useEffect, useState } from "react";
import { StateDropdown } from "@/design-system/Molecules/StateDropdown";
import { DASHBOARD_TABS } from "@/constants/dashboardTabs";
import { usePatientInformationService } from "@/rtkQuery/features/hooks/usePatientInformationService";
import { PhoneInput } from "@/design-system/Molecules/PhoneInput";
import { useAuthService } from "@/rtkQuery/features/hooks";
import { changePasswordSchema, type ChangePasswordSchema } from "@/validation-schemas/change-password-schemas";
import type { ButtonVariant } from "@/design-system/Atoms/Button/Button.types";
import { useScrollToTopOnMount } from "@/hooks/useScrollToTopOnMount";
import { toastService } from "@/utils/toastService";
interface UpdateProfileFormSectionProps {
  title?: string;
  saveButtonText?: string;
  showChangePasswordButton?: boolean;
  showEmailField?: boolean;
  submitButtonVariant?: ButtonVariant;
  showTitle?: boolean;
  onSubmitSuccess?: (args: {
    formData: UpdateProfileSchema;
    response: unknown;
  }) => void;
  onSubmitError?: (error: unknown) => void;
  skipApiIfUnchanged?: boolean;
}

export const UpdateProfileFormSection = ({
  title = "Update Your Patient Profile",
  saveButtonText = "Save Profile",
  showChangePasswordButton = true,
  showEmailField = true,
  submitButtonVariant = "darkSubmitButton",
  showTitle = true,
  onSubmitSuccess,
  onSubmitError,
  skipApiIfUnchanged = true,
}: UpdateProfileFormSectionProps) => {
  useScrollToTopOnMount();
  const navigate = useNavigate();
  const [showPasswordFields, setShowPasswordFields] = useState(false);
  const { getPatientInformation, patientEntitlements } =
    usePatientInformationService();

  const { changePassword, changePasswordIsLoading } = useAuthService();
  const {
    register,
    handleSubmit,
    control,
    formState: { errors, isSubmitting, isDirty },
    reset,
    getValues,
  } = useForm<UpdateProfileSchema>({
    resolver: zodResolver(updateProfileSchema),
  });

  const { submitPatientProfile, isLoading: isProfileLoading } =
    usePatientProfileFormSubmission();

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    formState: { errors: passwordErrors },
    reset: resetPasswordForm,
  } = useForm<ChangePasswordSchema>({
    resolver: zodResolver(changePasswordSchema),
  });

  const onChangePasswordSubmit = async (data: ChangePasswordSchema) => {
    try {
      await changePassword({
        password: data.password,
        newPassword: data.newPassword,
      });
      resetPasswordForm();
      setShowPasswordFields(false);
    } catch (err) {
      console.error("Change Password Failed", err);
    }
  };

  const onSubmit: SubmitHandler<UpdateProfileSchema> = async (data) => {
    try {

      if (skipApiIfUnchanged && !isDirty) {
        toastService.info("No changes made to your profile.");
        onSubmitSuccess?.({ formData: getValues(), response: undefined });
        return;
      }

      const response = await submitPatientProfile(data);
      onSubmitSuccess?.({ formData: data, response });
      if (!patientEntitlements?.HasQuestionnaire) {
        navigate(ROUTES.HEALTH_QUESTIONNAIRES);
      }
      reset();
    } catch (error) {
      onSubmitError?.(error);
      console.error("Update profile error", error);
    }
  };

  useEffect(() => {
    if (getPatientInformation) {
      const {
        FullName,
        DateOfBirth,
        Address,
        Address2,
        PhoneNumber,
        State,
        City,
        ZipCode,
        Email,
      } = getPatientInformation;

      const [firstName = "", lastName = ""] = (FullName || "").split(" ");
      const dob = DateOfBirth ? new Date(DateOfBirth) : undefined;
      reset({
        firstName,
        lastName,
        dateOfBirth: dob,
        phoneNumber: PhoneNumber || '',
        email: Email || '',
        address: Address || '',
        address2: Address2 || '',
        city: City || '',
        state: State || '',
        zipCode: ZipCode || '',
      });
    }
  }, [getPatientInformation, reset]);

  return (
    <div className="rounded-[20px] bg-[#f6f7f9] p-8" id="v-pills-tabContent">
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div
          id={DASHBOARD_TABS.UPDATE}
          role="tabpanel"
          aria-labelledby={DASHBOARD_TABS.UPDATE}
          className="block opacity-100"
        >
          {showTitle && (
            <Typography
              variant="darkBackgroundHeading"
              as="h3"
              className="animate-fade-in-up"
            >
              {title}
            </Typography>
          )}

          <Typography
            variant="sectionDescriptionSmall"
            as="h4"
            className="mb-5 text-[var(--color-gray-900)]"
          >
            Patient Details
          </Typography>
          <div className="flex flex-wrap mb-6 gap-y-6 gap-x-6">
            <div className="w-full lg:w-[calc(50%-0.75rem)]">
              <FloatingLabelInput
                id="firstName"
                label="First Name"
                type="text"
                {...register("firstName")}
                error={errors.firstName?.message}
              />
            </div>
            <div className="w-full lg:w-[calc(50%-0.75rem)]">
              <Controller
                name="lastName"
                control={control}
                render={({ field }) => (
                  <FloatingLabelInput
                    id="lastName"
                    label="Last Name"
                    type="text"
                    value={field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    error={errors.lastName?.message}
                  />
                )}
              />
            </div>

            <div className="w-full lg:w-[calc(50%-0.75rem)]">
              <Controller
                name="dateOfBirth"
                control={control}
                render={({ field }) => (
                  <FloatingLabelInput
                    id="dateOfBirth"
                    label="Date of Birth"
                    type="date"
                    value={
                      field.value
                        ? new Date(field.value).toISOString().slice(0, 10)
                        : ""
                    }
                    onChange={(e) => {
                      field.onChange(
                        e.target.value ? new Date(e.target.value) : undefined
                      );
                    }}
                    error={errors.dateOfBirth?.message}
                  />
                )}
              />
            </div>
            <div className="w-full lg:w-[calc(50%-0.75rem)]">
              <Controller
                name="phoneNumber"
                control={control}
                render={({ field }) => (
                  <PhoneInput
                    id="phoneNumber"
                    label="Phone Number"
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.phoneNumber?.message}
                  />
                )}
              />
            </div>

            {showEmailField && (
              <div className="relative w-full cursor-not-allowed">
                <div
                  className="peer form-control block w-full rounded-md border border-white bg-white px-7 pt-[26px] pb-2.5 text-base text-gray-900 cursor-not-allowed  break-words whitespace-pre-wrap"
                  id="email"
                >
                  {getPatientInformation?.Email || "N/A"}
                </div>
                <label
                  htmlFor="email"
                  className="absolute pl-[17px] pr-7 pb-4 left-3 top-2 text-sm transition-all peer-placeholder-shown:top-4 
                  peer-placeholder-shown:text-base peer-placeholder-shown:text-[#181a20] peer-focus:top-2 peer-focus:text-sm 
                  peer-focus:opacity-65 peer-not-placeholder-shown:opacity-65  cursor-not-allowed select-none"
                >
                  Email Address
                </label>
              </div>
            )}
          </div>
          <hr className="my-10 opacity-25" />
          <Typography
            variant="sectionDescriptionSmall"
            as="h4"
            className="mb-5 text-[var(--color-gray-900)]"
          >
            Address
          </Typography>
          <div className="flex flex-wrap mb-6 gap-y-6 gap-x-6">
            <div className="w-full lg:w-[calc(50%-0.75rem)]">
              <FloatingLabelInput
                id="address1"
                label="Address"
                type="text"
                {...register("address")}
                error={errors.address?.message}
              />
            </div>
            <div className="w-full lg:w-[calc(50%-0.75rem)]">
              <FloatingLabelInput
                id="address2"
                label="Address Line 2"
                type="text"
                {...register("address2")}
                error={errors.address2?.message}
              />
            </div>
          </div>
          <div className="flex flex-wrap mb-6 gap-y-6 gap-x-6">
            <div className="w-full lg:w-[calc(33.333%-1rem)]">
              <FloatingLabelInput
                id="city"
                label="City"
                type="text"
                {...register("city")}
                error={errors.city?.message}
              />
            </div>
            <div className="w-full lg:w-[calc(33.333%-1rem)]">
              <Controller
                name="state"
                control={control}
                render={({ field }) => (
                  <StateDropdown
                    id="state"
                    value={field.value}
                    onChange={field.onChange}
                    error={errors.state?.message}
                    placeholder="Select State"
                  />
                )}
              />
            </div>
            <div className="w-full lg:w-[calc(33.333%-1rem)]">
              <FloatingLabelInput
                id="zip"
                label="Zip"
                type="text"
                {...register("zipCode")}
                error={errors.zipCode?.message}
              />
            </div>
          </div>

          <hr className="my-10 opacity-25" />

          {showPasswordFields && (
            <>
              <Typography
                variant="sectionDescriptionSmall"
                as="h4"
                className="animate-fade-in-up  mb-5 text-[var(--color-gray-900)]"
              >
                Change Password
              </Typography>
              <div className="animate-fade-in-up  flex flex-wrap mb-6 gap-y-6 gap-x-6">
                <div className="w-full lg:w-[calc(50%-0.75rem)]">
                  <FloatingLabelInput
                    id="password"
                    label="Old Password"
                    type="password"
                    {...registerPassword("password")}
                    error={passwordErrors.password?.message}
                  />
                </div>
                <div className="w-full lg:w-[calc(50%-0.75rem)]">
                  <FloatingLabelInput
                    id="newPassword"
                    label="New Password"
                    type="password"
                    {...registerPassword("newPassword")}
                    error={passwordErrors.newPassword?.message}
                  />
                </div>
                <div className="w-full lg:w-[calc(50%-0.75rem)]">
                  <FloatingLabelInput
                    id="confirmPassword"
                    label="Confirm Password"
                    type="password"
                    {...registerPassword("confirmPassword")}
                    error={passwordErrors.confirmPassword?.message}
                  />
                </div>
              </div>
            </>
          )}

          <div className="flex flex-row items-center gap-6">
            {showChangePasswordButton && (
              <Button
                variant="secondary"
                size="xl"
                className="!w-full"
                type="button"
                onClick={
                  showPasswordFields
                    ? handlePasswordSubmit(onChangePasswordSubmit)
                    : () => setShowPasswordFields(true)
                }
                loading={changePasswordIsLoading}
              >
                {showPasswordFields
                  ? changePasswordIsLoading
                    ? "Updating..."
                    : "Update Password"
                  : "Change Password"}
              </Button>
            )}

            <Button
              variant={submitButtonVariant || "darkSubmitButton"}
              size="xl"
              className="!w-full"
              type="submit"
              disabled={isSubmitting || isProfileLoading}
              loading={isSubmitting || isProfileLoading}
            >
              {isProfileLoading ? "Updating..." : saveButtonText}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default UpdateProfileFormSection;
